import { Gauge, Loader2, Play, RotateCw, Square, Trash2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { availableActions, isDestructive, type AppAction } from '@/lib/appStatus';
import { cn } from '@/lib/utils';
import type { App } from '@/types/app';

// Change domain needs the domains and the app's latest switch: offered on the app page only.
type RowAction = Exclude<AppAction, 'change-domain'>;
const ACTION_META: Record<RowAction, { label: string; icon: LucideIcon }> = {
  scale: { label: 'Scale', icon: Gauge },
  stop: { label: 'Stop', icon: Square },
  start: { label: 'Start', icon: Play },
  redeploy: { label: 'Redeploy', icon: RotateCw },
  delete: { label: 'Delete', icon: Trash2 },
};

interface AppRowActionsProps {
  app: App;
  onAction: (action: AppAction, app: App) => void;
  isBusy: (appId: number, action?: AppAction) => boolean;
}

/** Icon buttons for the actions valid in the app's current status. Lives inside a table row. */
export function AppRowActions({ app, onAction, isBusy }: AppRowActionsProps) {
  const actions = availableActions(app.status).filter((a): a is RowAction => a !== 'change-domain');
  const name = app.display_name || app.name;

  if (actions.length === 0) {
    const text = app.status === 'migrating' ? 'Migrating…' : app.status === 'pending' ? 'Deploying…' : null;
    return text ? <span className="text-xs text-muted-foreground">{text}</span> : null;
  }

  return (
    <div className="flex items-center justify-end gap-0.5">
      {actions.map((action) => {
        const { label, icon: Icon } = ACTION_META[action];
        const busy = isBusy(app.id, action);
        const text = `${label} ${name}`;
        return (
          <Button
            key={action}
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={text}
            title={text}
            disabled={busy}
            className={cn(isDestructive(action) && 'text-destructive hover:text-destructive')}
            onClick={() => onAction(action, app)}
          >
            {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Icon aria-hidden="true" />}
          </Button>
        );
      })}
    </div>
  );
}
